import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import {
    Box,
    Button,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
} from '@mui/material';
import { StockTransferService } from '../../services/stockTransfer.service';
import {
    StockTransferDTO,
    STOCK_TRANSFER_STATUS_LABELS,
} from '../../interfaces/stockTransfer.interface';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';

const StockTransferDetails: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const toast = useToast();
    const [transfer, setTransfer] = useState<StockTransferDTO | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [actionInProgress, setActionInProgress] = useState<boolean>(false);

    const canManage = usePermission(PERMISSIONS.STOCK_TRANSFER_VIEW, true);

    const fetchTransfer = useCallback(async () => {
        if (!id) return;
        try {
            setLoading(true);
            const data = await StockTransferService.getStockTransferById(id);
            setTransfer(data);
        } catch (err) {
            console.error('Error fetching stock transfer:', err);
            toast.showApiError(err, 'Failed to load stock transfer');
        } finally {
            setLoading(false);
        }
    }, [id, toast]);

    useEffect(() => {
        fetchTransfer();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '-';
        return format(parseISO(dateString), 'dd-MM-yyyy');
    };

    const handleSubmitTransfer = async () => {
        if (!id) return;
        try {
            setActionInProgress(true);
            const updated = await StockTransferService.submitStockTransfer(id);
            setTransfer(updated);
            toast.showSuccess('Stock transfer submitted for approval');
        } catch (err) {
            console.error('Error submitting stock transfer:', err);
            toast.showApiError(err, 'Failed to submit stock transfer');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleApprove = async () => {
        if (!id) return;
        try {
            setActionInProgress(true);
            const updated = await StockTransferService.approveStockTransfer(id);
            setTransfer(updated);
            toast.showSuccess('Stock transfer approved');
        } catch (err) {
            console.error('Error approving stock transfer:', err);
            toast.showApiError(err, 'Failed to approve stock transfer');
        } finally {
            setActionInProgress(false);
        }
    };

    const handleCancel = async () => {
        if (!id) return;
        try {
            setActionInProgress(true);
            const updated = await StockTransferService.cancelStockTransfer(id);
            setTransfer(updated);
            toast.showSuccess('Stock transfer cancelled');
        } catch (err) {
            console.error('Error cancelling stock transfer:', err);
            toast.showApiError(err, 'Failed to cancel stock transfer');
        } finally {
            setActionInProgress(false);
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!transfer) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Stock transfer not found.</Typography>
                <Button sx={{ mt: 2 }} variant="outlined" onClick={() => navigate('/stock-transfers')}>
                    Back to List
                </Button>
            </Box>
        );
    }

    const isDraft = transfer.status === 'DRAFT';
    const isPendingApproval = transfer.status === 'PENDING_APPROVAL';

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">Stock Transfer {transfer.referenceNo}</Typography>
                <Button variant="outlined" onClick={() => navigate('/stock-transfers')}>
                    Back to List
                </Button>
            </Box>

            <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">From Store</Typography>
                        <Typography>{transfer.fromStoreName || '-'}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">To Store</Typography>
                        <Typography>{transfer.toStoreName || '-'}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <Typography>
                            {STOCK_TRANSFER_STATUS_LABELS[transfer.status] ?? transfer.status}
                        </Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Transfer Date</Typography>
                        <Typography>{formatDate(transfer.transferDate)}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Approved Date</Typography>
                        <Typography>{formatDate(transfer.approvedDate)}</Typography>
                    </Box>
                    <Box sx={{ minWidth: 160 }}>
                        <Typography variant="body2" color="text.secondary">Completed Date</Typography>
                        <Typography>{formatDate(transfer.completedDate)}</Typography>
                    </Box>
                </Box>
                {transfer.notes && (
                    <Box sx={{ mt: 3 }}>
                        <Typography variant="body2" color="text.secondary">Notes</Typography>
                        <Typography>{transfer.notes}</Typography>
                    </Box>
                )}
            </Paper>

            <TableContainer component={Paper} sx={{ mb: 3 }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                            <TableCell>#</TableCell>
                            <TableCell>Item Code</TableCell>
                            <TableCell>Item Name</TableCell>
                            <TableCell align="right">Quantity</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {transfer.lines.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center">
                                    No lines
                                </TableCell>
                            </TableRow>
                        ) : (
                            transfer.lines.map((line, index) => (
                                <TableRow key={line.id}>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>{line.itemCode}</TableCell>
                                    <TableCell>{line.itemName}</TableCell>
                                    <TableCell align="right">{line.quantity}</TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Box sx={{ display: 'flex', gap: 2 }}>
                {isDraft && canManage && (
                    <Button
                        variant="contained"
                        onClick={handleSubmitTransfer}
                        disabled={actionInProgress}
                    >
                        Submit for Approval
                    </Button>
                )}
                {isPendingApproval && canManage && (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleApprove}
                        disabled={actionInProgress}
                    >
                        Approve
                    </Button>
                )}
                {(isDraft || isPendingApproval) && canManage && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={handleCancel}
                        disabled={actionInProgress}
                    >
                        Cancel Transfer
                    </Button>
                )}
            </Box>
        </Box>
    );
};

export default StockTransferDetails;
